import { useAccountStorage } from '../../../../AppClients';
import { changeLoginEmailDef, getAccountPermissions, resetUserFactorDef, resetUserPasswordDef, } from '../../../../backend/features/accounts/shared';
import { allowCopyPassword, allowSwitchUser, allowUserSendMessagesAccountSlideout, requireAnySystemPermission, } from '../../../../backend/features/permissions/shared';
import { showDeleteEntityDialog } from '../../../../ui/components/showDeleteEntityDialog';
import { useChangeLoginEmailFormConfig } from '../../../../ui/features/accounts/hooks/useChangeLoginEmailFormConfig';
import { useContactManagementTranslation, useUserState } from '../../../../ui/hooks';
import { ContactManagementConfigContext, getUserStateResponseOptional } from '../../../../ui/providers';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorDialog, showAlertDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { showAlertSnackbar, showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { useFunctionClient, useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import { Typography } from '@mui/material';
import copy from 'copy-to-clipboard';
import md5 from 'md5';
import React from 'react';
import { useSelector } from 'react-redux';
import { accountSlideoutButtonIds, oktaStatusType } from '../components/AccountDetails/types';
export function useAccountSlideoutMenuItems({ mutateOktaStatus, accountReload, handleClose, oktaStatus, account, accountListReload, }) {
    const { t } = useContactManagementTranslation();
    const { notificationConfig } = React.useContext(ContactManagementConfigContext);
    const settings = useSelector(getSettings);
    const userClient = useUserClient();
    const userState = useUserState();
    const storageClient = useAccountStorage();
    const functionClient = useFunctionClient();
    const changeLoginEmailFormConfig = useChangeLoginEmailFormConfig();
    const handleChangeLoginEmail = useTypedPlatformFunction(changeLoginEmailDef);
    const resetUserPassword = useTypedPlatformFunction(resetUserPasswordDef);
    const resetUserFactor = useTypedPlatformFunction(resetUserFactorDef);
    const userStateResponse = useSelector(getUserStateResponseOptional);
    if (!account) {
        return [];
    }
    const accountPermissions = getAccountPermissions(settings, userState, account.id, account.data);
    const menuItems = [];
    if (allowSwitchUser(userState) && ['active', 'inactive'].includes(account.columns.systemStatus) && !(userStateResponse === null || userStateResponse === void 0 ? void 0 : userStateResponse.switchUserPrev)) {
        menuItems.push({
            label: t('panels.accounts.switchToThisUser'),
            id: accountSlideoutButtonIds.switchToThisUser,
            onClick: () => {
                userClient
                    .switchUser({
                    provider: 'switch',
                    user: account.columns.email,
                })
                    .then((response) => {
                    window.location.assign(response.loginSuccessUrl);
                })
                    .catch(handleErrorDialog);
            },
            'data-test': 'menu-item-switch-to-user',
        });
    }
    // TODO: remove after PFCM-1551
    if ((notificationConfig === null || notificationConfig === void 0 ? void 0 : notificationConfig.onAccountNotificationSend) &&
        account.data.systemStatus === 'active' &&
        allowUserSendMessagesAccountSlideout(userState, account.columns.orgMembership)) {
        menuItems.push({
            label: t('panels.accounts.sendNotification'),
            id: accountSlideoutButtonIds.sendNotification,
            onClick: () => { var _a; return (_a = notificationConfig === null || notificationConfig === void 0 ? void 0 : notificationConfig.onAccountNotificationSend) === null || _a === void 0 ? void 0 : _a.call(notificationConfig, [account.id]); },
            'data-test': 'menu-item-send-message',
        });
    }
    if (allowCopyPassword(userState) && /^auto__[A-Za-z0-9]+_[A-Za-z0-9]+@example.com$/.test(account.columns.email)) {
        menuItems.push({
            label: t('panels.accounts.copyPassword'),
            id: accountSlideoutButtonIds.copyPassword,
            onClick: () => {
                const password = md5(account.columns.email.toLowerCase() + 'bent-bird-69');
                copy(password);
                showInfoSnackbar({ message: t('panels.accounts.passwordCopiedToClipboard'), autoHideDuration: 2000 });
            },
            'data-test': 'menu-item-copy-password',
        });
    }
    if (accountPermissions.allowDelete && requireAnySystemPermission(userState, 'manage-accounts')) {
        menuItems.push({
            label: t('common.delete'),
            id: accountSlideoutButtonIds.delete,
            onClick: () => {
                showDeleteEntityDialog({
                    entity: t('panels.accounts.entity'),
                    t: t,
                    onConfirm: () => {
                        storageClient
                            .post({
                            action: 'partial-update',
                            id: account.id,
                            data: { systemStatus: 'deleted' },
                        })
                            .then(() => {
                            showInfoSnackbar({ message: t('common.itemDeleted', { item: account.columns.displayName }) });
                            handleClose(true);
                        })
                            .catch(handleErrorDialog);
                    },
                });
            },
            'data-test': 'menu-item-delete',
        });
    }
    if (settings.identityProvider.oktaActionsEnabled && typeof oktaStatus === 'string') {
        if (requireAnySystemPermission(userState, 'allow-reset-okta-mfa')) {
            menuItems.push({
                label: t('panels.accounts.resetAuthenticationFactors'),
                id: accountSlideoutButtonIds.resetAuthenticationFactors,
                onClick: () => {
                    showConfirmDialog({
                        title: t('panels.accounts.resetMfaForUser', { accountName: account.columns.displayName }),
                        onConfirm: () => {
                            resetUserFactor({ accountId: account.id })
                                .then(() => {
                                showAlertSnackbar({
                                    message: t('panels.accounts.authenticationFactorIsReset'),
                                    type: 'success',
                                    autoHideDuration: 2000,
                                });
                            })
                                .catch(handleErrorDialog);
                        },
                        confirmLabel: t('panels.accounts.reset'),
                    });
                },
                'data-test': 'menu-reset-mfa',
            });
        }
        if (requireAnySystemPermission(userState, 'allow-reset-okta-password-email')) {
            menuItems.push({
                label: t('panels.accounts.resetEmailPassword'),
                id: accountSlideoutButtonIds.resetEmailPassword,
                onClick: () => {
                    showConfirmDialog({
                        title: t('panels.accounts.resetPasswordForUser', { accountName: account.columns.displayName }),
                        onConfirm: () => {
                            resetUserPassword({ accountId: account.id })
                                .then(() => {
                                showAlertSnackbar({
                                    message: t('panels.accounts.passwordIsReset'),
                                    type: 'success',
                                    autoHideDuration: 2000,
                                });
                                mutateOktaStatus();
                            })
                                .catch(handleErrorDialog);
                        },
                        confirmLabel: t('panels.accounts.send'),
                    });
                },
                'data-test': 'menu-reset-password',
            });
        }
        if (requireAnySystemPermission(userState, 'allow-unlock-okta-account') && oktaStatus === oktaStatusType.LOCKED_OUT) {
            menuItems.push({
                label: t('panels.accounts.unlockAccount'),
                id: accountSlideoutButtonIds.unlockAccount,
                onClick: () => {
                    showConfirmDialog({
                        title: t('panels.accounts.unlockAccountForUser', { accountName: account.columns.displayName }),
                        onConfirm: () => {
                            functionClient
                                .executeFunction({
                                functionName: 'unlockAccount',
                                params: {
                                    accountId: account.id,
                                },
                            })
                                .then(() => {
                                showAlertSnackbar({
                                    message: t('panels.accounts.accountIsUnlocked'),
                                    type: 'success',
                                    autoHideDuration: 2000,
                                });
                                mutateOktaStatus();
                            })
                                .catch(handleErrorDialog);
                        },
                        confirmLabel: t('panels.accounts.unlock'),
                    });
                },
                'data-test': 'menu-unlock-account',
            });
        }
        if (settings.identityProvider.oktaActionsEnabled && requireAnySystemPermission(userState, 'allow-change-login-email')) {
            menuItems.push({
                label: t('panels.accounts.changeLoginEmail'),
                id: accountSlideoutButtonIds.changeLoginEmail,
                onClick: () => {
                    showFormDialog({
                        title: t('changeLoginEmailDialog.title', { displayName: account.columns.displayName }),
                        config: changeLoginEmailFormConfig,
                        data: { currentEmailAddress: account.columns.email },
                        onSubmit: (data) => {
                            handleChangeLoginEmail({
                                accountId: account.id,
                                newEmail: data.newEmailAddress,
                            })
                                .then(() => {
                                showAlertDialog({
                                    title: t('changeLoginEmailDialog.saveAlert.title'),
                                    content: (React.createElement(Typography, { variant: 'body1', color: themeColors.text.subtle }, t('changeLoginEmailDialog.saveAlert.content', {
                                        currentEmail: data.currentEmailAddress,
                                        newEmail: data.newEmailAddress,
                                    }))),
                                    closeLabel: t('common.close'),
                                    onClose: () => {
                                        accountListReload === null || accountListReload === void 0 ? void 0 : accountListReload();
                                        accountReload();
                                    },
                                });
                            })
                                .catch(handleErrorDialog);
                        },
                        submitLabel: t('common.save'),
                    });
                },
                'data-test': 'menu-unlock-account',
            });
        }
    }
    return menuItems;
}
